import type { Engine } from '../sim/engine.js';
import type { Renderer } from '../render/renderer.js';
import type { MockEventSource } from '../sources/mock.js';
import type { TeamId } from '@rekrut/shared';
import type { Sound } from '../audio/sound.js';

export interface DevHooks {
  getSpeed(): number;
  setSpeed(v: number): void;
  getFps(): number;
}

/**
 * Дев-панель. В эфире скрыта (?dev=0), но существует не ради удобства:
 * баланс осады настраивается только на живом поле.
 *
 * Отдельно показывается темп впуска из очереди против темпа убийств — это
 * то самое соотношение, от которого зависит, может ли одна сторона вообще
 * продавить фронт и дойти до чужого трона.
 */
export class DevPanel {
  private readonly el: HTMLElement;
  private readonly stats: HTMLElement;
  private killWindow: number[] = [];
  private lastKills = 0;

  constructor(
    private readonly engine: Engine,
    private readonly renderer: Renderer,
    private readonly mock: MockEventSource,
    private readonly sound: Sound,
    private readonly hooks: DevHooks,
  ) {
    this.el = document.getElementById('devpanel')!;
    this.el.innerHTML = '';

    // Тема матча прямо на месте: имя и лицо команды решают, за кого писать
    // в чат. «Месси или Роналду» читается мгновенно, «A или B» — нет.
    this.section('Команды');
    this.teamControls('A');
    this.teamControls('B');

    this.section('Звук');
    this.buttons([
      ['Вкл / выкл', (b) => b.classList.toggle('on', !this.sound.toggle())],
      ['Проверить', () => {
        this.sound.resume();
        this.sound.boom(0, 1);
      }],
    ]);
    this.slider('Громкость, %', 0, 100, 5, () => this.sound.getVolume() * 100, (v) => this.sound.setVolume(v / 100));

    this.section('Матч');
    this.buttons([
      ['Рестарт', () => this.engine.restart()],
      ['Пауза', (b) => this.togglePause(b)],
      ['×1', () => this.hooks.setSpeed(1)],
      ['×2', () => this.hooks.setSpeed(2)],
      ['×4', () => this.hooks.setSpeed(4)],
    ]);

    this.section('Наполнение');
    this.buttons([
      ['+20 A', () => this.mock.sendJoins(20, 'A')],
      ['+20 B', () => this.mock.sendJoins(20, 'B')],
      ['+100', () => this.mock.sendJoins(100)],
      ['+300', () => this.mock.sendJoins(300)],
    ]);

    // Две строки, потому что источник выстрела принципиально разный: боец на
    // поле бьёт со своего места, а тот, кто ещё в очереди, — со своей
    // аватарки в колонне. Без раздельных кнопок проверить поле почти
    // невозможно: случайный донатер почти всегда оказывается в очереди.
    this.section('Оружие — с поля боя');
    this.buttons([
      ['1 🌹', () => this.giftFrom('field', 1)],
      ['10', () => this.giftFrom('field', 10)],
      ['50', () => this.giftFrom('field', 50)],
      ['500', () => this.giftFrom('field', 500)],
      ['2000', () => this.giftFrom('field', 2000)],
      ['5000', () => this.giftFrom('field', 5000)],
    ]);

    this.section('Оружие — из очереди');
    this.buttons([
      ['1 🌹', () => this.giftFrom('queue', 1)],
      ['10', () => this.giftFrom('queue', 10)],
      ['50', () => this.giftFrom('queue', 50)],
      ['500', () => this.giftFrom('queue', 500)],
      ['2000', () => this.giftFrom('queue', 2000)],
      ['5000', () => this.giftFrom('queue', 5000)],
    ]);

    this.section('Поток событий');
    this.slider('Интенсивность', 0, 5, 0.1, () => this.mock.settings.intensity, (v) => (this.mock.settings.intensity = v));
    this.slider('Подарков/мин', 0, 200, 1, () => this.mock.settings.giftsPerMin, (v) => (this.mock.settings.giftsPerMin = v));
    this.slider('Вступлений/мин', 0, 400, 5, () => this.mock.settings.joinsPerMin, (v) => (this.mock.settings.joinsPerMin = v));
    this.slider('Лайков/сек', 0, 200, 1, () => this.mock.settings.likesPerSec, (v) => (this.mock.settings.likesPerSec = v));

    this.section('Очередь и поле');
    this.slider('Впуск, бойцов/с', 0.1, 8, 0.1, () => this.engine.cfg.queue.entriesPerSec, (v) => (this.engine.cfg.queue.entriesPerSec = v));
    this.slider('Потолок живых', 50, 500, 10, () => this.engine.cfg.queue.maxAlive, (v) => (this.engine.cfg.queue.maxAlive = v));
    this.slider('Плотность от', 50, 500, 10, () => this.engine.cfg.density.refCount, (v) => (this.engine.cfg.density.refCount = v));

    this.section('Бой');
    this.slider('Урон', 1, 20, 1, () => this.engine.cfg.unit.damage, (v) => (this.engine.cfg.unit.damage = v));
    this.slider('Интервал, мс', 200, 3000, 50, () => this.engine.cfg.unit.shotIntervalMs, (v) => (this.engine.cfg.unit.shotIntervalMs = v));
    this.slider('Дальность', 150, 900, 10, () => this.engine.cfg.unit.range, (v) => (this.engine.cfg.unit.range = v));
    this.slider('Скорость', 10, 200, 2, () => this.engine.cfg.unit.speed, (v) => (this.engine.cfg.unit.speed = v));
    this.slider('Фокус на трон, %', 0, 100, 5, () => this.engine.cfg.unit.throneFocus * 100, (v) => (this.engine.cfg.unit.throneFocus = v / 100));

    this.section('Трон');
    this.slider('HP трона', 1000, 40000, 500, () => this.engine.cfg.throne.hp, (v) => this.setThroneHp(v));
    this.slider('Реген/с', 0, 20, 1, () => this.engine.cfg.throne.regenPerSec, (v) => (this.engine.cfg.throne.regenPerSec = v));
    this.slider('Отжать на, px', 20, 400, 5, () => this.engine.cfg.throne.restoreGap, (v) => (this.engine.cfg.throne.restoreGap = v));
    this.slider('Полоса, px', 0, 300, 5, () => this.engine.cfg.arena.bufferWidth, (v) => (this.engine.cfg.arena.bufferWidth = v));
    this.slider('Стоп у трона', 60, 500, 5, () => this.engine.cfg.arena.frontMargin, (v) => (this.engine.cfg.arena.frontMargin = v));
    this.slider('Отклик фронта', 1, 5, 0.1, () => this.engine.cfg.arena.frontResponse, (v) => (this.engine.cfg.arena.frontResponse = v));

    this.stats = document.createElement('div');
    this.stats.id = 'devstats';
    this.el.appendChild(this.stats);
  }

  toggle(): void {
    this.el.classList.toggle('hidden');
  }

  update(dtMs: number): void {
    const s = this.engine.state;
    const cfg = this.engine.cfg;

    // Темп убийств за последние ~5 секунд — против темпа впуска.
    const dk = s.stats.kills - this.lastKills;
    this.lastKills = s.stats.kills;
    this.killWindow.push(dk / (dtMs / 1000));
    if (this.killWindow.length > 20) this.killWindow.shift();
    const killRate = this.killWindow.reduce((a, b) => a + b, 0) / this.killWindow.length / 2;

    const active = s.projectiles.reduce((n, p) => n + (p.active ? 1 : 0), 0);
    const r = this.renderer.stats();
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    const thA = s.thrones.A;
    const thB = s.thrones.B;

    this.stats.innerHTML =
      `<b>${this.hooks.getFps().toFixed(0)} fps</b>   ×${this.hooks.getSpeed()}\n` +
      `фаза     ${s.phase}\n` +
      `живых    A ${this.engine.countAlive('A')}  B ${this.engine.countAlive('B')}  (${s.unitList.length})\n` +
      `очередь  A ${s.teams.A.queue.length}  B ${s.teams.B.queue.length}\n` +
      `масштаб  ×${s.densityScale.toFixed(2)}\n` +
      `трон A   ${thA.hp.toFixed(0)} ${thA.shielded ? 'щит' : '<b>ОСАДА</b>'} защ ${thA.defenders}\n` +
      `трон B   ${thB.hp.toFixed(0)} ${thB.shielded ? 'щит' : '<b>ОСАДА</b>'} защ ${thB.defenders}\n` +
      `<b>убийств/с ${killRate.toFixed(2)}</b> против впуска ${cfg.queue.entriesPerSec.toFixed(1)}\n` +
      `звук     ${this.sound.state()}\n` +
      `снаряды  ${active} / ${s.projectiles.length}\n` +
      `текстуры ав ${r.avatars} тек ${r.texts}\n` +
      (mem ? `память   ${(mem.usedJSHeapSize / 1048576).toFixed(0)} МБ` : '');
  }

  // ─── построение UI ─────────────────────────────────────────────────────

  private section(title: string): void {
    const h = document.createElement('h3');
    h.textContent = title;
    this.el.appendChild(h);
  }

  private buttons(items: [string, (btn: HTMLButtonElement) => void][]): void {
    const row = document.createElement('div');
    row.className = 'row';
    for (const [label, fn] of items) {
      const b = document.createElement('button');
      b.textContent = label;
      b.onclick = () => fn(b);
      row.appendChild(b);
    }
    this.el.appendChild(row);
  }

  private slider(
    label: string,
    min: number,
    max: number,
    step: number,
    get: () => number,
    set: (v: number) => void,
  ): void {
    const l = document.createElement('label');
    const span = document.createElement('span');
    span.textContent = label;
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(get());
    const out = document.createElement('output');
    out.textContent = fmt(get());
    input.oninput = () => {
      const v = Number(input.value);
      set(v);
      out.textContent = fmt(v);
    };
    l.append(span, input, out);
    this.el.appendChild(l);
  }

  /** Подарок от конкретного зрителя: с поля боя или из очереди. */
  private giftFrom(where: 'field' | 'queue', diamonds: number): void {
    const s = this.engine.state;
    const pool =
      where === 'field'
        ? s.unitList
        : [...s.teams.A.queue, ...s.teams.B.queue];
    if (pool.length === 0) return;
    const p = pool[Math.floor(Math.random() * pool.length)]!;
    this.mock.sendGift(diamonds, {
      userId: p.userId,
      uniqueId: p.userId,
      nickname: p.nickname,
      avatarUrl: p.avatarUrl,
      isSubscriber: false,
      isModerator: false,
    });
  }

  /** Название и портрет команды: текст и файл с диска. */
  private teamControls(team: TeamId): void {
    const cfg = this.engine.cfg.teams[team];

    const row = document.createElement('label');
    const span = document.createElement('span');
    span.textContent = team === 'A' ? 'Верх' : 'Низ';

    const name = document.createElement('input');
    name.type = 'text';
    name.value = cfg.label;
    name.oninput = () => {
      const v = name.value.toUpperCase();
      cfg.label = v;
      // Матч копирует названия при старте — правим и текущий, чтобы
      // изменение было видно сразу, а не со следующего боя.
      this.engine.state.teams[team].label = v;
    };

    const file = document.createElement('input');
    file.type = 'file';
    file.accept = 'image/*';
    file.onchange = () => {
      const f = file.files?.[0];
      if (!f) return;
      this.renderer.setTeamImage(team, URL.createObjectURL(f));
    };

    row.append(span, name);
    this.el.append(row, file);
  }

  private togglePause(btn: HTMLButtonElement): void {
    const on = this.hooks.getSpeed() !== 0;
    this.hooks.setSpeed(on ? 0 : 1);
    btn.classList.toggle('on', on);
  }

  /** Меняем потолок трона вместе с текущим HP, иначе ползунок бесполезен. */
  private setThroneHp(v: number): void {
    const s = this.engine.state;
    this.engine.cfg.throne.hp = v;
    for (const t of ['A', 'B'] as const) {
      const th = s.thrones[t];
      const frac = th.hp / th.maxHp;
      th.maxHp = v;
      th.hp = v * frac;
    }
  }
}

function fmt(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
